import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { isSafeImageUrl } from '../../utils/sanitize';
import { optimizeImage } from '../../utils/imageProcessing';
import './ImageLightbox.css';

/**
 * Produce a cropped image as a base64 data URL.
 * Returns a raw PNG data-URL; caller should run optimizeImage() on it.
 */
function getCroppedImg(image, crop) {
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  canvas.width = crop.width * scaleX;
  canvas.height = crop.height * scaleY;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    canvas.width,
    canvas.height
  );
  return canvas.toDataURL('image/png');
}

const ImageLightbox = ({ imageUrl, isOpen, onClose, isEditMode, onCropSave }) => {
  const [isCropping, setIsCropping] = useState(false);
  const [crop, setCrop] = useState(undefined);
  const [completedCrop, setCompletedCrop] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const imgRef = useRef(null);

  const handleStartCrop = () => {
    setCrop({ unit: '%', x: 10, y: 10, width: 80, height: 80 });
    setCompletedCrop(null);
    setIsCropping(true);
  };

  const handleCancelCrop = () => {
    setIsCropping(false);
    setCrop(undefined);
    setCompletedCrop(null);
  };

  const handleApplyCrop = async () => {
    if (!completedCrop || !imgRef.current) return;
    if (completedCrop.width < 1 || completedCrop.height < 1) return;
    setIsProcessing(true);
    try {
      const rawCropped = getCroppedImg(imgRef.current, completedCrop);
      // Resize & compress the cropped result (max 1920×1080, JPEG)
      const optimized = await optimizeImage(rawCropped);
      setIsCropping(false);
      setCrop(undefined);
      setCompletedCrop(null);
      if (onCropSave) onCropSave(optimized);
    } catch (err) {
      console.error('Crop failed:', err);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClose = () => {
    setIsCropping(false);
    setCrop(undefined);
    setCompletedCrop(null);
    onClose();
  };

  if (!isOpen || !imageUrl || !isSafeImageUrl(imageUrl)) return null;

  return (
    <div className="lightbox-overlay">
      <button type="button" className="lightbox-backdrop" onClick={handleClose} aria-label="Lightbox schließen" />
      <dialog className="lightbox-container" open aria-label="Bildvorschau">
        {/* Top-right buttons */}
        <div className="lightbox-actions">
          {isEditMode && !isCropping && (
            <button
              className="lightbox-btn lightbox-btn-crop"
              onClick={handleStartCrop}
              title="Bild zuschneiden"
            >
              <i className="bi bi-crop"></i> Zuschneiden
            </button>
          )}
          <button className="lightbox-btn lightbox-btn-close" onClick={handleClose} title="Schließen">
            ✕
          </button>
        </div>

        {isCropping ? (
          /* ---- Crop mode ---- */
          <div className="lightbox-crop-wrapper">
            <div className="lightbox-crop-area">
              <ReactCrop
                crop={crop}
                onChange={(c) => setCrop(c)}
                onComplete={(c) => setCompletedCrop(c)}
              >
                <img
                  ref={imgRef}
                  src={imageUrl}
                  alt="Crop-Vorschau"
                  className="lightbox-crop-img"
                />
              </ReactCrop>
            </div>
            <div className="lightbox-crop-controls">
              <div className="lightbox-crop-btns">
                <button className="btn btn-success btn-sm" onClick={handleApplyCrop} disabled={isProcessing}>
                  {isProcessing
                    ? <><span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>Wird optimiert…</>
                    : <><i className="bi bi-check-lg"></i> Übernehmen</>}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={handleCancelCrop} disabled={isProcessing}>
                  Abbrechen
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* ---- Normal view ---- */
          <img
            className="lightbox-image"
            src={imageUrl}
            alt="Task-Bild (vergrößert)"
            onClick={(e) => e.stopPropagation()}
            role="presentation"
          />
        )}
      </dialog>
    </div>
  );
};

ImageLightbox.propTypes = {
  imageUrl: PropTypes.string,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
  onCropSave: PropTypes.func,
};

export default ImageLightbox;
